import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RoutesService } from 'src/app/api/routes.service';
import { AuthService } from '../../api/auth.service';
import { LangService } from '../../core/lang.service';
import { WhitelabelService } from '../../domain/whitelabel.service';
import { CaptureImageUploadService } from 'src/app/ui-item-maker/capture-image/capture-image-upload.service';
import { ElementType, QuestionState } from '../models';
import { QuestionPubSub } from '../question-runner/pubsub/question-pubsub';
import { IContentElementVideo, IEntryStateVideo } from './model';
import { ZoomService } from '../../ui-testrunner/zoom.service';


export const memo = <T>(map: Map<string, T>, key:string, generator: (key:string) => T ) => {
  if (map.has(key)){
    return map.get(key);
  }
  else{
    const val = generator(key);
    map.set(key, val);
    return val;
  }
}

const typesFromExt = {mp4: 'video/mp4', webm: 'video/webm', ogg: 'video/ogg'};
const MP4_BUCKET = 'https://eqao.vretta.com/authoring'

@Component({
  selector: 'element-render-video',
  templateUrl: './element-render-video.component.html',
  styleUrls: ['./element-render-video.component.scss']
})
export class ElementRenderVideoComponent implements OnInit, OnDestroy {

  @Input() element:IContentElementVideo;
  @Input() isLocked:boolean;
  @Input() isShowSolution:boolean;
  @Input() questionState:QuestionState;
  @Input() questionPubSub?: QuestionPubSub;
  @Input() allowSubtitles: boolean = false;
  @Input() allowTranscripts: boolean = false;
  @Input() allowPlaybackSpeed: boolean = false;

  @ViewChild('video')  video: ElementRef
  @ViewChild('videoTooltip')  videoTooltip: ElementRef
  @ViewChild('videoPlayer') videoPlayer: ElementRef

  isTooltipVisible: boolean = false; 
  public plyrOptions = {
    controls: [
      'play-large', 
      'play', 
      'progress', 
      'current-time', 
      'mute', 
      'volume', 
      'captions', 
      'settings', 
      'fullscreen'
    ]
  }
  private sanitizedUrls:Map<string, SafeResourceUrl> = new Map();
  private videoSources:Map<string, any[]> = new Map();
  private subtitleTracks:Map<string, any[]> = new Map();
  public videoSourceUrls:string[] = [];


  constructor(
    private sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private whitelabel: WhitelabelService,
    private uploadService: CaptureImageUploadService,
    private http: HttpClient,
    private auth: AuthService,
    private routes: RoutesService,
    public zoom: ZoomService,
    public lang: LangService,
  ) { }

  isAutoPlay = false;
  startTimeStamp = 0;
  isLimitted:boolean;
  playBackRate = 1.0
  playBackRates = [
    1.0, 0.9, 0.75, 0.5, 0.25
  ]

  transcriptUrl
  ngOnInit() {
    if (this.element.assetId) {
      this.auth.apiGet(this.routes.TEST_AUTH_ASSET, this.element.assetId).then((res)=>{
        if (!res) {
          return
        }
        if (this.lang.getCurrentLanguage()=='en' && res["transcript_en"]) {
          this.transcriptUrl = res["transcript_en"]
        } else if (this.lang.getCurrentLanguage()=='fr' && res["transcript_fr"]) {
          this.transcriptUrl = res["transcript_fr"]
        }
      })
    } else {
      this.transcriptUrl = this.element.transcriptUrl
    }
    this.isPlaying = false;
    this.isLimitted = !(this.whitelabel.getSiteFlag('IS_EQAO') && !this.element.isRestricted);
    this.ensureState();
    this.initVars();

    this.uploadService.newUpload.subscribe(() => {
      this.videoSourceUrls = this.getVideoSources();
    });

    if (this.element.isAutoPlay) {
      this.initVideoCountdown();
    }
  }

  initVars() {
    const id = this.element.entryId
    if (this.questionState){
      if (this.questionState[id]) {
        const qState = this.questionState[id]
        if (!qState.seekTimes) {
          qState.seekTimes = []
        }
        if (!qState.loadTimes) {
          qState.loadTimes = []
        }
        if (!qState.pauseTimes) {
          qState.pauseTimes = []
        }
        if (!qState.endedTimes) {
          qState.endedTimes = []
        }
      }
      this.questionState[id].loadTimes.push(this.getTime())
    }

    this.videoSourceUrls = this.getVideoSources();
  }

  ngAfterViewInit() {
    
  }

  getCleanURL(url) {
    if (!url) return;
    const newURL = this.sanitizer.bypassSecurityTrustResourceUrl(url)
    return newURL
  }

  transcriptOn = false;
  transcript = "";
  showTranscript() {
    if (this.transcriptUrl) {
      this.transcriptOn = !this.transcriptOn;
    } 

    this.http.get(this.transcriptUrl).subscribe(res => {
      if (!res["text"]) return
      this.transcript = res["text"]
    }, error => {
      // Log the failed fetch event
      this.auth.apiCreate(this.routes.LOG, {
        slug: 'CLIENT_FETCH_ERROR_VIDEO',
        data: {
          requestURL: this.transcriptUrl,
          error: error.message
        }
      })
    });
  }

  adjustRate() {
    const el = this.getVideoElement()
    if (el) {
      el.playbackRate = this.playBackRate
    }
  }

  ngOnDestroy() {
    try{
      const el = this.getVideoElement();
      // console.log('el to destroy', el)
      el.pause();
    } catch(e) {}
  }

  isCountdownInProgress: boolean;
  countdownInterval: any;
  countdownTimeRemaining: number;
  initVideoCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.isCountdownInProgress = true;
    this.countdownTimeRemaining = this.element.autoPlayDelay;
    this.countdownInterval = setInterval(() => {
      if (!this.isCountdownInProgress) {
        return;
      }
      if (this.countdownTimeRemaining > 0) {
        this.countdownTimeRemaining--;
      }
      if (this.countdownTimeRemaining <= 0) {
        this.isCountdownInProgress = false;
        this.play();
      }
    }, 1000);
  }


  canStartPlayingCalled = false;
  canStartPlaying() {
    if (!this.canStartPlayingCalled && this.questionState && this.canPlay()) {
      const id = this.element.entryId
      const el = this.getVideoElement()
      const ts = this.questionState[id].timeStamp;
      this.startTimeStamp = ts;
      if (!el) return;
      if (ts && ts>0 && ts<el["duration"]) {
        this.isAutoPlay = true;
        el["currentTime"] = ts;
        el.play();
      }
      this.canStartPlayingCalled = true;
    }
    
  }

  resetter = true;
  resetFlag() {
    this.resetter = false;
    this.changeDetector.detectChanges();
    this.resetter = true;
  }

  getTime() {
    const d = new Date();
    return d.getTime();
  }

  getVideoElement() {
    // return this.video.nativeElement
    const id = this.element.entryId
    let el = null;
    
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      el = this.videoPlayer.nativeElement
    }
    return el;
  }

  ensureState() {
    if (this.questionState && !this.questionState[this.element.entryId]) {
      const state:IEntryStateVideo = {
        isStarted: true,
        isFilled: true,
        isResponded: false,
        numViews: 0,
        timeStamp: 0,
        type: ElementType.VIDEO
      };
      if (!state.numViews) {
        state.numViews = 0;
      }
      this.questionState[this.element.entryId] = state;
    }
    if (this.questionState){
      return this.questionState[this.element.entryId];
    }
    else{
      return {}
    }
  }

  private isSafari(){
    const userAgent = window.navigator.userAgent;
    return userAgent.indexOf("Safari") > -1;
  }
  
  loadedMetaData() {
    if (this.isSafari()) {
      this.canStartPlaying()
    }
  }


  getSecRemainingStr() {
    const element = this.getVideoElement();
    if (!element) {
      return '';
    }

    const leadingZero = (n:number) => {
      if ((''+n).length < 2){
        return '0'+n
      }
      return ''+n
    }
    const convertSecToMinSec = (sec) => {
      if (!sec ){
        return '00:00'
      }
      const min = Math.floor(sec/60);
      return leadingZero(min)+':'+leadingZero(Math.floor(sec - min*60))
    }

    const remainingSeconds = () => {
      return (element["duration"] - element["currentTime"]) | 0
    }
    // return convertSecToMinSec(element["currentTime"]) + ' / ' + convertSecToMinSec(element["duration"])
    return remainingSeconds()
  }

  hasStarted() {
    return this.isPlaying
  }

  getSubtitleTracks(){
    return memo(this.subtitleTracks, this.element.subtitlesUrl, src => {
      return [{
        kind: 'subtitles',
        label: this.lang.getCurrentLanguage() == 'fr' ? 'French': 'English',
        srclang: this.lang.getCurrentLanguage(),
        src: src ? this.optimizeUrl(src) : '',
        default: true,
      }]
    });
  }

  getVideoSources() {
    console.log('getVideoSources', this.element.urls)
    if(this.element.urls && this.element.urls.length > 0) {
      return this.optimizeTypes(this.element.urls);
    }

    // to prevent "this.element.url is null" error
    if (!this.element.url && this.element.urls.length === 0) {
      console.warn("no url provided for video");
      return [];
    }

    if (!this.element.url) return [];

    // HOTFIX: Attempt to add alternative video sources
    const filePath = this.element.url.split(MP4_BUCKET).pop();
    const file = filePath.split('/').pop();

    const altUrlAuto   = `${MP4_BUCKET}/video${filePath}.webm`; // If the video was auto-converted after reuploading in authoring but not yet published
    const altUrlManual = `${MP4_BUCKET}/video/${file}.webm`;    // If the video was manually converted (never reuploaded)
    return this.optimizeTypes([this.element.url, altUrlAuto, altUrlManual]);
  }

  optimizeTypes(urls:string[]) {
    console.log('optimizeTypes', urls)
    const optimized = [];

    for(let src of urls) {
      if(!src) {
        continue;
      }
      const ext = src.split('.').pop();
      const type = typesFromExt[ext];

      if (type) {
        optimized.push({ src: (ext == "mp4" ? this.optimizeUrl(src) : src), type });
      }
    }
    return optimized
  }

  optimizeUrl(url:string){
    const domainRaw:string = 'https://eqao.vretta.com/authoring/';
    const domainCdn:string = 'https://eqao.vretta.com/authoring/';
    return url.split(domainRaw).join(domainCdn);
  }

  getVideoUrl(){
    return memo(this.sanitizedUrls, this.element.url, rawUrl => this.sanitizer.bypassSecurityTrustResourceUrl(this.optimizeUrl(rawUrl)) );
  }

  isPlaying;
  playing() {  
    this.isPlaying = true;
    this.questionState[this.element.entryId].isResponded = true;
    if(this.questionState && this.questionState[this.element.entryId].numViews === 0) {
      this.isTooltipVisible = true;
    }
  }
  
  fullScreen() {
    const elem = this.getVideoElement()
    if (elem && this.element.allowFullScreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
      }
    }
  }

  play() {
    this.isPlaying = true;
    this.isAutoPlay = false;
    const el:HTMLMediaElement = this.getVideoElement();
    if (!el) return;
    if (this.canPlay()) {
      el.play();
      this.addView();
    }
  }

  notPlaying(logTime:boolean = true) {
    this.isPlaying = false;
    this.isAutoPlay = false;
    if (logTime) {
      if (this.questionState && this.questionState[this.element.entryId]) {
        this.questionState[this.element.entryId].pauseTimes.push(this.getTime());
      }
    }
    // console.log("pausing times", this.questionState[this.element.entryId].pauseTimes)
  }

  addView() {
    if (this.questionState && this.questionState[this.element.entryId]) {
      const state = this.questionState[this.element.entryId]
      const el = this.getVideoElement();
      if (!el) return;
      if (!this.isAutoPlay && (!state.timeStamp || state.timeStamp==0 || state.timeStamp==el["duration"])){
        state.numViews++;
        state.timeStamp = 0;
        this.resetFlag();
      }
    }
  }

  ended() {
    this.notPlaying(false);
    if (this.questionState && this.questionState[this.element.entryId]) {
      this.questionState[this.element.entryId].endedTimes.push(this.getTime());
    }
    console.log("ended times", this.questionState[this.element.entryId].endedTimes)
    if (this.element.onVideoEnd) {
      this.element.onVideoEnd.forEach(pub => {
        this.questionPubSub.elementPub(+pub.entryId, pub.type, pub.data);
      });
    }
  }

  canPlay() {
    const state = this.ensureState();
    const el = this.getVideoElement()
    if (!el) {
      return false;
    }
    if (state){
      if (this.element.maxViews && ((state["timeStamp"] == 0 || state["timeStamp"].toFixed(3)==el["duration"].toFixed(3))&& state.numViews >= this.element.maxViews)){
        return false;
      }
      else{
        return true;
      }
    }
    return false;  
  }

  dismissTooltip() {
    this.isTooltipVisible = false;
  }

  timeSet = false;
  onTimeUpdate() {
    if (this.questionState && this.questionState[this.element.entryId]) {
      const element = this.getVideoElement();
      if (!element) return;
      const state = this.questionState[this.element.entryId]
      if (state.timeStamp && state.timeStamp > element["currentTime"] && !this.timeSet) {
        element["currentTime"] = state.timeStamp
        this.timeSet = true
      } else {
        state.timeStamp = element["currentTime"]
      }
    }
  }

  onSeeking() {
    if (this.questionState && this.questionState[this.element.entryId]) {
      if (!this.questionState[this.element.entryId].seekTimes) {
        this.questionState[this.element.entryId].seekTimes = []
      }
      this.questionState[this.element.entryId].seekTimes.push(this.getTime())
    }
  }

  getZoom = () => this.zoom.getZoom();
}
